import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { FormSelect } from '../ui/Select'
import { ALL_TYPE_OPTIONS, COMPARE_TYPES } from './constants'
import DateRangePicker from './DateRangePicker'

const EMPTY_OPTIONS = []

const FilterForm = ({
  initialStartDate,
  initialEndDate,
  onDateRangeChange,
  variableOptions,
  questionOptions,
  gradeQuestionOptions,
  numericQuestionOptions,
  selectedVariable,
  selectedQuestions,
  compareType,
  displayMode,
  displayOptions,
  hasSelectionQuestions,
  hasGradeQuestions,
  hasNumericQuestions,
  showPeriodFilter,
  onVariableChange,
  onQuestionChange,
  onQuestionsChange,
  onCompareTypeChange,
  onDisplayChange
}) => {
  const typeOptions = ALL_TYPE_OPTIONS.filter(opt =>
    opt.value === COMPARE_TYPES.SELECTION && hasSelectionQuestions
    || opt.value === COMPARE_TYPES.GRADE && hasGradeQuestions
    || opt.value === COMPARE_TYPES.NUMERIC && hasNumericQuestions
  )
  const showTypeSelector = typeOptions.length > 0

  const hideDisplaySelector = compareType === COMPARE_TYPES.NUMERIC

  const questionSelectorConfig = {
    [COMPARE_TYPES.GRADE]: {
      label: 'Notes (colonnes)', options: gradeQuestionOptions, multi: true
    },
    [COMPARE_TYPES.NUMERIC]: { label: 'Question numérique', options: numericQuestionOptions },
    [COMPARE_TYPES.SELECTION]: { label: 'Question (colonnes)', options: questionOptions }
  }
  const { label: questionLabel, options: questionSelectorOptions, multi } =
    questionSelectorConfig[compareType] || questionSelectorConfig[COMPARE_TYPES.SELECTION]

  const questionSelector = multi ? (
    <FormSelect
      options={questionSelectorOptions}
      placeholder="Choisir..."
      value={selectedQuestions}
      closeMenuOnSelect={false}
      isClearable
      isMulti
      showSelectAll
      onChange={onQuestionsChange}
    />
  ) : (
    <FormSelect
      options={questionSelectorOptions}
      placeholder="Choisir..."
      value={selectedQuestions[0] || null}
      isClearable
      onChange={onQuestionChange}
    />
  )

  return (
    <div>
      <div className="row">
        {showTypeSelector ? (
          <div className="col-6 col-md mb-2">
            <label>Type</label>
            <FormSelect
              options={typeOptions}
              value={typeOptions.find(opt => opt.value === compareType)}
              onChange={onCompareTypeChange}
            />
          </div>
        ) : null}
        {hideDisplaySelector ? null : (
          <div className="col-6 col-md mb-2">
            <label>Afficher</label>
            <FormSelect
              options={displayOptions}
              value={displayOptions.find(opt => opt.value === displayMode)}
              onChange={onDisplayChange}
            />
          </div>
        )}
        {showPeriodFilter ? (
          <DateRangePicker
            initialEndDate={initialEndDate}
            initialStartDate={initialStartDate}
            onChange={onDateRangeChange}
          />
        ) : null}
      </div>
      <div className="row mt-1 pt-2 border-top">
        <div className="col-12 col-md-6 mb-2">
          <label>Variable (lignes)</label>
          <FormSelect
            options={variableOptions}
            placeholder="Choisir..."
            value={selectedVariable}
            isClearable
            onChange={onVariableChange}
          />
        </div>
        <div className="col-12 col-md-6 mb-2">
          <label>{questionLabel}</label>
          {questionSelector}
        </div>
      </div>
    </div>
  )
}

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired
})

const groupedOptionsShape = PropTypes.arrayOf(PropTypes.shape({
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired
}))

FilterForm.propTypes = {
  compareType: PropTypes.string.isRequired,
  displayMode: PropTypes.string.isRequired,
  displayOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  gradeQuestionOptions: groupedOptionsShape,
  hasGradeQuestions: PropTypes.bool,
  hasNumericQuestions: PropTypes.bool,
  hasSelectionQuestions: PropTypes.bool,
  numericQuestionOptions: groupedOptionsShape,
  onCompareTypeChange: PropTypes.func.isRequired,
  onDisplayChange: PropTypes.func.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  onQuestionsChange: PropTypes.func.isRequired,
  onVariableChange: PropTypes.func.isRequired,
  questionOptions: groupedOptionsShape.isRequired,
  selectedQuestions: PropTypes.arrayOf(optionShape),
  selectedVariable: optionShape,
  showPeriodFilter: PropTypes.bool,
  variableOptions: PropTypes.arrayOf(optionShape).isRequired
}

FilterForm.defaultProps = {
  gradeQuestionOptions: EMPTY_OPTIONS,
  hasGradeQuestions: false,
  hasNumericQuestions: false,
  hasSelectionQuestions: false,
  numericQuestionOptions: EMPTY_OPTIONS,
  selectedQuestions: EMPTY_OPTIONS,
  selectedVariable: null,
  showPeriodFilter: true
}

export default memo(FilterForm)
